/**
 * Partner Drivers Tab — Invite + Revoke drivers
 */
import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Users, UserPlus, UserX, Loader2, CheckCircle, Mail, Phone, X, RefreshCw, AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import config from 'config';

const API = `${config.API_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const STATUS_BADGE = {
  ACTIVE: { label: 'Actif', cls: 'text-green-400 bg-green-500/20' },
  PENDING_VERIFICATION: { label: 'En attente', cls: 'text-amber-400 bg-amber-500/20' },
  SUSPENDED: { label: 'Suspendu', cls: 'text-red-400 bg-red-500/20' },
};

export default function PartnerDriversTab({ drivers, onRefresh }) {
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', first_name: '', last_name: '', phone: '' });
  const [inviting, setInviting] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [resending, setResending] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const [resendResult, setResendResult] = useState(null);

  const handleInvite = async () => {
    if (!inviteForm.email || !inviteForm.first_name || !inviteForm.last_name) {
      alert('Email, prénom et nom requis');
      return;
    }
    setInviting(true);
    setInviteResult(null);
    try {
      const res = await axios.post(`${API}/partner/drivers/invite`, inviteForm, { headers: getHeaders() });
      setInviteResult(res.data);
      setInviteForm({ email: '', first_name: '', last_name: '', phone: '' });
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
    setInviting(false);
  };

  const handleResendInvite = async (driverId) => {
    setResending(driverId);
    setResendResult(null);
    try {
      const res = await axios.post(`${API}/partner/drivers/${driverId}/resend-invite`, {}, { headers: getHeaders() });
      setResendResult({ driverId, ...res.data });
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors du renvoi');
    }
    setResending(null);
  };

  const handleRevoke = async (driverId, driverName) => {
    if (!window.confirm(`Révoquer ${driverName} de votre flotte ?`)) return;
    setRevoking(driverId);
    try {
      await axios.post(`${API}/partner/drivers/${driverId}/revoke`, {}, { headers: getHeaders() });
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
    setRevoking(null);
  };

  return (
    <div className="space-y-4" data-testid="drivers-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-white font-semibold flex items-center gap-2">
          <Users className="w-5 h-5 text-[#C9A24D]" /> Chauffeurs ({drivers.length})
        </h2>
        <Button onClick={() => { setShowInvite(!showInvite); setInviteResult(null); }}
          className="bg-[#C9A24D] hover:bg-[#b8913f] text-black" data-testid="invite-driver-btn">
          <UserPlus className="w-4 h-4 mr-1" /> Inviter
        </Button>
      </div>

      {/* Invite Form */}
      {showInvite && (
        <Card className="bg-[#1F1F1F] border-[#C9A24D]/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white text-sm font-medium">Inviter un chauffeur</h3>
              <button onClick={() => setShowInvite(false)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-white/50 text-[10px] mb-1 block">Prénom *</Label>
                <Input value={inviteForm.first_name} onChange={e => setInviteForm({ ...inviteForm, first_name: e.target.value })}
                  className="bg-[#0B0B0B] border-[#C9A24D]/20 text-white h-9 text-xs" data-testid="invite-first-name" />
              </div>
              <div>
                <Label className="text-white/50 text-[10px] mb-1 block">Nom *</Label>
                <Input value={inviteForm.last_name} onChange={e => setInviteForm({ ...inviteForm, last_name: e.target.value })}
                  className="bg-[#0B0B0B] border-[#C9A24D]/20 text-white h-9 text-xs" data-testid="invite-last-name" />
              </div>
              <div>
                <Label className="text-white/50 text-[10px] mb-1 block">Email *</Label>
                <Input type="email" value={inviteForm.email} onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="bg-[#0B0B0B] border-[#C9A24D]/20 text-white h-9 text-xs" data-testid="invite-email" />
              </div>
              <div>
                <Label className="text-white/50 text-[10px] mb-1 block">Téléphone</Label>
                <Input value={inviteForm.phone} onChange={e => setInviteForm({ ...inviteForm, phone: e.target.value })}
                  className="bg-[#0B0B0B] border-[#C9A24D]/20 text-white h-9 text-xs" data-testid="invite-phone" />
              </div>
            </div>
            <Button onClick={handleInvite} disabled={inviting} className="mt-3 bg-[#C9A24D] hover:bg-[#b8913f] text-black text-xs h-8" data-testid="send-invite-btn">
              {inviting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Mail className="w-3 h-3 mr-1" />}
              Envoyer l'invitation
            </Button>

            {inviteResult && (
              <div className="mt-3 p-3 bg-green-500/10 border border-green-500/20 rounded" data-testid="invite-result">
                <p className="text-green-400 text-xs font-medium flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5" /> {inviteResult.message}
                </p>
                <p className="text-white/50 text-xs mt-1">
                  {inviteResult.email_sent
                    ? "Un email d'activation a été envoyé au chauffeur pour qu'il définisse son mot de passe."
                    : "Email non envoyé — le chauffeur devra contacter le support pour activer son compte."}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Driver List */}
      {drivers.length === 0 ? (
        <Card className="bg-[#1F1F1F] border-[#C9A24D]/10">
          <CardContent className="p-8 text-center">
            <Users className="w-10 h-10 text-[#C9A24D]/20 mx-auto mb-3" />
            <p className="text-white/40 text-sm">Aucun chauffeur dans votre flotte</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {drivers.map(d => {
            const st = STATUS_BADGE[d.driver_status] || STATUS_BADGE.PENDING_VERIFICATION;
            const comp = d.compliance;
            const nonCompliant = comp && comp.has_blocking_issues;
            return (
              <Card key={d.id} className="bg-[#1F1F1F] border-[#C9A24D]/10" data-testid={`driver-card-${d.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-[#C9A24D]/10 flex items-center justify-center text-[#C9A24D] text-xs font-bold shrink-0">
                        {(d.first_name?.[0] || '') + (d.last_name?.[0] || '')}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white text-sm font-medium">{d.first_name} {d.last_name}</p>
                          <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${st.cls}`}>{st.label}</span>
                          {nonCompliant && (
                            <span className="text-[10px] px-2 py-0.5 rounded font-medium text-orange-400 bg-orange-500/20 flex items-center gap-1"
                              data-testid={`compliance-alert-${d.id}`} title="Documents/profil à régulariser">
                              <AlertTriangle className="w-3 h-3" /> Non conforme
                            </span>
                          )}
                          {d.online && <span className="w-2 h-2 rounded-full bg-green-400 shrink-0" title="En ligne" />}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-3 text-xs text-white/40 mt-0.5">
                          {d.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3 shrink-0" /><span className="truncate">{d.email}</span></span>}
                          {d.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3 shrink-0" />{d.phone}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {d.needs_password_setup && (
                        <Button variant="ghost" size="sm" onClick={() => handleResendInvite(d.id)}
                          disabled={resending === d.id}
                          className="text-[#C9A24D]/60 hover:text-[#C9A24D] hover:bg-[#C9A24D]/10 h-7 w-7 p-0" data-testid={`resend-invite-${d.id}`}
                          title="Renvoyer l'invitation">
                          {resending === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleRevoke(d.id, `${d.first_name} ${d.last_name}`)}
                        disabled={revoking === d.id}
                        className="text-red-400/60 hover:text-red-400 hover:bg-red-500/10 h-7 w-7 p-0" data-testid={`revoke-driver-${d.id}`}>
                        {revoking === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserX className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  </div>
                  {/* Resend result inline */}
                  {resendResult && resendResult.driverId === d.id && (
                    <div className="mt-2 p-2 bg-green-500/10 border border-green-500/20 rounded text-xs" data-testid={`resend-result-${d.id}`}>
                      <p className="text-green-400 font-medium flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> {resendResult.message}
                      </p>
                      <p className="text-white/50 mt-1">
                        {resendResult.email_sent
                          ? "Un nouvel email d'activation a été envoyé."
                          : "Email non envoyé — le chauffeur devra contacter le support."}
                      </p>
                    </div>
                  )}
                  {/* Compliance issues detail */}
                  {nonCompliant && comp.issues?.length > 0 && (
                    <div className="mt-2 p-2 bg-orange-500/10 border border-orange-500/20 rounded text-xs" data-testid={`compliance-detail-${d.id}`}>
                      <p className="text-orange-400 font-medium flex items-center gap-1 mb-1">
                        <AlertTriangle className="w-3 h-3" /> Conformité — à régulariser
                      </p>
                      <ul className="text-white/60 space-y-0.5">
                        {comp.issues.map((iss, idx) => (
                          <li key={idx}>• {iss.label}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
